import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Presentation, ExternalLink, Power, CheckCircle, XCircle, Search, Filter, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import PortalLayout from '../../../components/PortalLayout';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import axios from 'axios';

const LandingPageManagement = () => {
  const { token } = useAuth();
  const toast = useToast();
  const [pages, setPages] = useState([]);
  const [filteredPages, setFilteredPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive
  const [currentPage, setCurrentPage] = useState(1);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, pageId: null, pageTitle: '', currentStatus: false });
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, pageId: null, pageTitle: '' });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Use window.location.origin for preview URLs so it works in both dev and production
  const SITE_URL = window.location.origin;

  // Pagination settings
  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.ceil(filteredPages.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentPages = filteredPages.slice(startIndex, endIndex);

  useEffect(() => {
    fetchPages();
  }, []);

  useEffect(() => {
    filterAndSortPages();
  }, [pages, searchTerm, statusFilter]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/portal/landing-pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast.error('Error loading landing pages');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortPages = () => {
    let filtered = [...pages];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(page =>
        page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        page.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (page.description && page.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(page =>
        statusFilter === 'active' ? page.is_active : !page.is_active
      );
    }

    // Sort by latest created (newest first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA; // Descending order (newest first)
    });

    setFilteredPages(filtered);
  };

  const toggleStatus = async (pageId, currentStatus, pageTitle) => {
    setConfirmDialog({ show: true, pageId, pageTitle, currentStatus });
  };

  const confirmToggle = async () => {
    const { pageId } = confirmDialog;
    try {
      await axios.put(`${BACKEND_URL}/api/portal/landing-pages/${pageId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfirmDialog({ show: false, pageId: null, pageTitle: '', currentStatus: false });
      toast.success('Landing page status updated successfully');
      fetchPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error toggling status');
    }
  };

  const cancelToggle = () => {
    setConfirmDialog({ show: false, pageId: null, pageTitle: '', currentStatus: false });
  };

  const deletePage = (pageId, pageTitle) => {
    setDeleteConfirm({ isOpen: true, pageId, pageTitle });
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/portal/landing-pages/${deleteConfirm.pageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteConfirm({ isOpen: false, pageId: null, pageTitle: '' });
      toast.success('Landing page deleted successfully');
      fetchPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting landing page');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <PortalLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Landing Page Management</h1>
          <p className="text-gray-600">Manage campaign landing pages and their visibility</p>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title, slug, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active Only</SelectItem>
                  <SelectItem value="inactive">Inactive Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Results Count */}
            <div className="flex items-center text-sm text-gray-600">
              Showing {currentPages.length} of {filteredPages.length} pages
            </div>
          </div>
        </div>

        {/* Landing Pages Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Title</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Slug</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Created</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {currentPages.map((page) => (
                <tr key={page.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="text-sm font-semibold text-gray-900">{page.title}</div>
                    {page.description && (
                      <div className="text-xs text-gray-500 mt-1 line-clamp-1">{page.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    <code className="px-2 py-1 bg-gray-100 rounded text-xs">/landing/{page.slug}</code>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {page.is_active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <XCircle className="w-3 h-3 mr-1" />Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    <div>{formatDate(page.created_at)}</div>
                    {page.created_by && (
                      <div className="text-xs text-gray-400">by {page.created_by}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <a href={`${SITE_URL}/landing/${page.slug}`} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-50">
                          <ExternalLink className="w-4 h-4 mr-1" />Preview
                        </Button>
                      </a>
                      <Button 
                        onClick={() => toggleStatus(page.id, page.is_active, page.title)} 
                        size="sm" 
                        className={page.is_active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
                      >
                        <Power className="w-4 h-4 mr-1" />{page.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button 
                        onClick={() => deletePage(page.id, page.title)} 
                        size="sm" 
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredPages.length === 0 && !loading && (
          <div className="text-center py-12 text-gray-500 bg-white rounded-xl border border-gray-200 mt-6">
            <Presentation className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium mb-2">No landing pages found</p>
            <p className="text-sm text-gray-400">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'No landing pages yet. Create your first campaign landing page!'}
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between bg-white rounded-xl border border-gray-200 px-6 py-4">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages} ({filteredPages.length} total results)
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
                className="disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              
              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      className={`px-3 py-1 rounded ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } transition-colors`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <Button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                variant="outline"
                size="sm"
                className="disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        {confirmDialog.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {confirmDialog.currentStatus ? 'Deactivate' : 'Activate'} Landing Page?
              </h2>
              <p className="text-gray-700 mb-2">
                Are you sure you want to {confirmDialog.currentStatus ? 'deactivate' : 'activate'} <strong>"{confirmDialog.pageTitle}"</strong>?
              </p>
              <p className="text-sm text-gray-600 mb-6">
                {confirmDialog.currentStatus 
                  ? '⚠️ The landing page will become inaccessible to visitors. Anyone trying to access it will see an error message.' 
                  : '✅ The landing page will be published and accessible to anyone with the link.'}
              </p>
              <div className="flex gap-3">
                <Button onClick={confirmToggle} className={confirmDialog.currentStatus ? 'flex-1 bg-red-600 hover:bg-red-700' : 'flex-1 bg-green-600 hover:bg-green-700'}>
                  {confirmDialog.currentStatus ? 'Deactivate' : 'Activate'}
                </Button>
                <Button onClick={cancelToggle} variant="outline" className="flex-1">Cancel</Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={deleteConfirm.isOpen}
          onClose={() => setDeleteConfirm({ isOpen: false, pageId: null, pageTitle: '' })}
          onConfirm={confirmDelete}
          title="Delete Landing Page?"
          message={`Are you sure you want to permanently delete "${deleteConfirm.pageTitle}"? This action cannot be undone and the page will be removed from the sitemap.`}
          confirmText="Delete Page"
          cancelText="Cancel"
          type="danger"
        />
      </div>
    </PortalLayout>
  );
};

export default LandingPageManagement;
